// app/frontend/components/ui/Badges/CountBadge.tsx
//
// The standard count chip ("Mon, Oct 19 [3]"). CLAUDE.md: counts go in a
// badge, never parentheses. A StatusPill with tabular figures so counts line
// up across rows.
import React from 'react';
import { StatusPill } from './StatusPill';
import { COUNT_ON_ACCENT } from '../../../src/constants/theme';
import { themeAlpha, themeVar } from '../../../src/utils/pageTheme';

export interface CountBadgeProps {
  count: number | string;
  /** Read by screen readers, e.g. "3 sessions". Defaults to the number. */
  ariaLabel?: string;
  /** "attention" tints the chip orange for counts that need acting on. */
  tone?: 'neutral' | 'attention';
  /** Tint from the page theme's accent instead (orange on an unthemed page). */
  themed?: boolean;
  /** Sitting on a solid orange surface (an active tab): dark chip, white figure. */
  onAccent?: boolean;
  /** Solid accent fill with a white figure, for a count that must read at a glance. */
  solid?: boolean;
  /** Lets a surface size the chip, e.g. scale it with the text beside it. */
  className?: string;
}

const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  ariaLabel,
  tone = 'neutral',
  onAccent = false,
  themed = false,
  solid = false,
  className,
}) => (
  <StatusPill
    tone={tone}
    size="xs"
    aria-label={ariaLabel}
    className={className}
    style={{
      fontVariantNumeric: 'tabular-nums',
      ...(onAccent ? { background: COUNT_ON_ACCENT.bg, color: COUNT_ON_ACCENT.fg } : null),
      ...(themed ? { background: themeAlpha(0.16), color: themeVar('badge-text') } : null),
      // The deepened accent keeps white text at 5:1 (#2431).
      ...(solid ? { background: themeVar('white-badge-fill'), color: '#ffffff', fontWeight: 600 } : null),
    }}
  >
    {count}
  </StatusPill>
);

export default CountBadge;
